import React, { Fragment } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { classNames } from "Utils/utils";
import { PaEmptyState } from "components/platform-admin";
import { formatMetadataJson, hasExpandableMetadata } from "./auditLog.utils";

const MetadataExpandRow = ({ metadata, colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="bg-gray-50 px-4 py-3">
      <pre className="max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-800">
        {formatMetadataJson(metadata)}
      </pre>
    </td>
  </tr>
);

const PlatformAuditLogTable = ({
  columns,
  rows,
  expandedRowId,
  onToggleExpand,
  isLoading = false,
  emptyState = null,
}) => {
  const renderEmpty = () =>
    emptyState || <PaEmptyState title="No audit events found" message="Try adjusting your filters" />;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={classNames(
                    "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500",
                    column.headerClassName
                  )}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`loading-${index}`}>
                    {columns.map((column) => (
                      <td key={column.key} className="px-4 py-4">
                        <div className="h-4 animate-pulse rounded bg-gray-100" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}

            {!isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length || 1} className="px-4 py-8">
                  {renderEmpty()}
                </td>
              </tr>
            ) : null}

            {!isLoading
              ? rows.map((row) => {
                  const isExpanded = expandedRowId === row.id;
                  const canExpand = hasExpandableMetadata(row.metadata);

                  return (
                    <Fragment key={row.id}>
                      <tr className="transition-colors hover:bg-indigo-50/40">
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className={classNames(
                              "px-4 py-3 text-sm text-gray-700",
                              column.key === "metadata" ? "" : "whitespace-nowrap",
                              column.cellClassName
                            )}
                          >
                            {column.key === "metadata" ? (
                              <button
                                type="button"
                                disabled={!canExpand}
                                onClick={() => onToggleExpand(row.id)}
                                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:text-gray-400"
                              >
                                {isExpanded ? (
                                  <HiChevronUp className="h-4 w-4" aria-hidden="true" />
                                ) : (
                                  <HiChevronDown className="h-4 w-4" aria-hidden="true" />
                                )}
                                {isExpanded ? "Hide JSON" : "View JSON"}
                              </button>
                            ) : typeof column.render === "function" ? (
                              column.render(row)
                            ) : (
                              row?.[column.key]
                            )}
                          </td>
                        ))}
                      </tr>
                      {isExpanded && canExpand ? (
                        <MetadataExpandRow metadata={row.metadata} colSpan={columns.length} />
                      ) : null}
                    </Fragment>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlatformAuditLogTable;
